import React, { useEffect, useState } from 'react';
import { FaExternalLinkAlt, FaGlobe, FaLandmark, FaBalanceScale, FaTrophy, FaShieldAlt } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import Footer from '../components/Footer';
import PageCover from '../components/PageCover';

// Logos : priorité aux fichiers locaux /images/links/<key>.png, sinon URLs officielles (Wikimedia, favicons)
const LOGOS = {
  ftf: 'https://www.google.com/s2/favicons?domain=ftf.org.tn&sz=128',
  ministereSport: 'https://www.google.com/s2/favicons?domain=sport.tn&sz=128',
  cnot: 'https://www.google.com/s2/favicons?domain=cnot.org.tn&sz=128',
  fifa: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/84/FIFA_Logo_%28R%29_%282010%29.svg/200px-FIFA_Logo_%28R%29_%282010%29.svg.png',
  caf: 'https://upload.wikimedia.org/wikipedia/sco/thumb/7/72/Confederation_of_African_Football_logo.svg/200px-Confederation_of_African_Football_logo.svg.png',
  unaf: 'https://upload.wikimedia.org/wikipedia/en/thumb/b/b9/UNAF_%28logo%29.png/200px-UNAF_%28logo%29.png',
  ifab: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/89/International_Football_Association_Board.png/200px-International_Football_Association_Board.png',
  tas: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/ef/Court_of_Arbitration_for_Sport_logo.svg/200px-Court_of_Arbitration_for_Sport_logo.svg.png',
  wada: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/WADA_logo.svg/200px-WADA_logo.svg.png'
};

// Fichiers locaux optionnels (si présents dans public/images/links/, ils sont utilisés en priorité)
const LOCAL_LOGO_KEYS = {
  ftf: 'ftf',
  ministereSport: 'ministere-sport',
  cnot: 'cnot',
  fifa: 'fifa',
  caf: 'caf',
  unaf: 'unaf',
  ifab: 'ifab',
  tas: 'tas',
  wada: 'wada'
};

const SECTIONS = [
  {
    id: 'national',
    titleFr: 'Instances nationales',
    titleAr: 'الجهات الوطنية',
    icon: FaLandmark,
    links: [
      { nameFr: 'Fédération Tunisienne de Football (FTF)', nameAr: 'الجامعة التونسية لكرة القدم', descFr: 'Site officiel de la FTF', descAr: 'الموقع الرسمي للجامعة التونسية لكرة القدم', url: 'https://www.ftf.org.tn/', logo: LOGOS.ftf, logoKey: 'ftf' },
      { nameFr: 'Ministère de la Jeunesse et des Sports (Tunisie)', nameAr: 'وزارة الشباب والرياضة (تونس)', descFr: 'Portail officiel du Ministère', descAr: 'البوابة الرسمية لوزارة الشباب والرياضة', url: 'http://www.sport.tn/', logo: LOGOS.ministereSport, logoKey: 'ministereSport' },
      { nameFr: 'Comité National Olympique Tunisien (CNOT)', nameAr: 'اللجنة الأولمبية الوطنية التونسية', descFr: 'Site officiel du CNOT', descAr: 'الموقع الرسمي للجنة الأولمبية التونسية', url: 'https://www.cnot.org.tn/', logo: LOGOS.cnot, logoKey: 'cnot' }
    ]
  },
  {
    id: 'international',
    titleFr: 'Instances internationales',
    titleAr: 'الجهات الدولية',
    icon: FaGlobe,
    links: [
      { nameFr: 'FIFA', nameAr: 'الاتحاد الدولي لكرة القدم', descFr: 'Fédération Internationale de Football Association', descAr: 'الاتحاد الدولي لكرة القدم', url: 'https://www.fifa.com/', logo: LOGOS.fifa, logoKey: 'fifa' },
      { nameFr: 'Confédération Africaine de Football (CAF)', nameAr: 'الاتحاد الأفريقي لكرة القدم', descFr: 'Site officiel de la CAF', descAr: 'الموقع الرسمي للاتحاد الأفريقي لكرة القدم', url: 'https://www.cafonline.com/', logo: LOGOS.caf, logoKey: 'caf' },
      { nameFr: 'Union Nord-Africaine de Football (UNAF)', nameAr: 'اتحاد شمال أفريقيا لكرة القدم', descFr: 'Site officiel de l\'UNAF', descAr: 'الموقع الرسمي لاتحاد شمال أفريقيا', url: 'https://www.unaf-football.org/', logo: LOGOS.unaf, logoKey: 'unaf' },
      { nameFr: 'International Football Association Board (IFAB)', nameAr: 'مجلس الاتحاد الدولي لكرة القدم', descFr: 'Gardien des Lois du Jeu', descAr: 'حارس قوانين اللعبة', url: 'https://www.theifab.com/', logo: LOGOS.ifab, logoKey: 'ifab' }
    ]
  },
  {
    id: 'justice',
    titleFr: 'Justice sportive',
    titleAr: 'العدالة الرياضية',
    icon: FaBalanceScale,
    links: [
      { nameFr: 'Tribunal Arbitral du Sport (TAS / CAS)', nameAr: 'محكمة التحكيم الرياضي', descFr: 'Cour internationale d\'arbitrage sportif', descAr: 'المحكمة الدولية للتحكيم الرياضي', url: 'https://www.tas-cas.org/', logo: LOGOS.tas, logoKey: 'tas' },
      { nameFr: 'Chambre de Résolution des Litiges FIFA', nameAr: 'غرفة فض النزاعات في الفيفا', descFr: 'Médiation et arbitrage FIFA', descAr: 'الوساطة والتحكيم في الفيفا', url: 'https://www.fifa.com/legal', logo: LOGOS.fifa, logoKey: 'fifa' }
    ]
  },
  {
    id: 'competitions',
    titleFr: 'Compétitions officielles',
    titleAr: 'المسابقات الرسمية',
    icon: FaTrophy,
    links: [
      { nameFr: 'Coupe du Monde FIFA', nameAr: 'كأس العالم FIFA', descFr: 'Tournoi mondial des nations', descAr: 'البطولة العالمية للدول', url: 'https://www.fifa.com/fifaplus/en/tournaments/mens/worldcup', logo: LOGOS.fifa, logoKey: 'fifa' },
      { nameFr: 'Coupe d\'Afrique des Nations (CAN)', nameAr: 'كأس أمم أفريقيا', descFr: 'Championnat d\'Afrique des nations', descAr: 'بطولة أمم أفريقيا', url: 'https://www.cafonline.com/total-africa-cup-of-nations/', logo: LOGOS.caf, logoKey: 'caf' },
      { nameFr: 'Ligue des Champions CAF', nameAr: 'دوري أبطال أفريقيا', descFr: 'Compétition clubs africains', descAr: 'مسابقة أندية أفريقيا', url: 'https://www.cafonline.com/totalenergies-caf-champions-league/', logo: LOGOS.caf, logoKey: 'caf' }
    ]
  },
  {
    id: 'integrity',
    titleFr: 'Intégrité & Règlements',
    titleAr: 'النزاهة واللوائح',
    icon: FaShieldAlt,
    links: [
      { nameFr: 'Code disciplinaire FIFA', nameAr: 'اللوائح التأديبية FIFA', descFr: 'Règlement et discipline FIFA', descAr: 'اللوائح والانضباط في الفيفا', url: 'https://www.fifa.com/legal/football-regulatory', logo: LOGOS.fifa, logoKey: 'fifa' },
      { nameFr: 'Agence Mondiale Antidopage (WADA)', nameAr: 'الوكالة العالمية لمكافحة المنشطات', descFr: 'Règles antidopage internationales', descAr: 'القواعد الدولية لمكافحة المنشطات', url: 'https://www.wada-ama.org/', logo: LOGOS.wada, logoKey: 'wada' }
    ]
  }
];

function getLogoUrl(link) {
  if (!link.logo) return null;
  const localKey = link.logoKey ? (LOCAL_LOGO_KEYS[link.logoKey] || link.logoKey) : null;
  return localKey ? `/images/links/${localKey}.png` : null;
}

function LinkCard({ link, section, isAr }) {
  const localSrc = getLogoUrl(link);
  const [logoSrc, setLogoSrc] = useState(localSrc || link.logo);
  const [logoError, setLogoError] = useState(false);
  const showLogo = logoSrc && !logoError;
  const handleLogoError = () => {
    if (localSrc && logoSrc === localSrc && link.logo) {
      setLogoSrc(link.logo);
    } else {
      setLogoError(true);
    }
  };
  return (
    <article style={styles.card} className="useful-link-card">
      <div style={styles.cardLogo}>
        {showLogo ? (
          <img
            src={logoSrc}
            alt=""
            style={styles.cardLogoImg}
            onError={handleLogoError}
          />
        ) : (
          <section.icon style={styles.cardLogoIcon} />
        )}
      </div>
      <h3 style={styles.cardTitle}>{isAr ? link.nameAr : link.nameFr}</h3>
      <p style={styles.cardDesc}>{isAr ? link.descAr : link.descFr}</p>
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        style={styles.cardBtn}
        className="useful-link-btn"
      >
        {isAr ? 'زيارة الموقع' : 'Visiter le site'}
        <FaExternalLinkAlt style={styles.cardBtnIcon} />
      </a>
    </article>
  );
}

const UsefulLinksPage = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  useEffect(() => {
    const title = isAr ? 'روابط مفيدة | الرابطة الوطنية للهواة' : 'Infos utiles | Ligue Nationale Amateur';
    const description = isAr
      ? 'روابط رسمية للجهات الوطنية والدولية لكرة القدم: FTF، الفيفا، CAF، العدالة الرياضية، المسابقات والنزاهة.'
      : 'Liens officiels des instances nationales et internationales du football : FTF, FIFA, CAF, justice sportive, compétitions et intégrité.';
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = description;
    return () => { document.title = 'الرابطة الوطنية - Ligue Amateur'; };
  }, [isAr]);

  return (
    <div style={styles.container}>
      <PageCover
        title={isAr ? 'روابط مفيدة' : 'Infos utiles'}
        subtitle={isAr
          ? 'الرابطة الوطنية لكرة القدم للهواة - المستوى الثاني | روابط الجهات الرسمية الوطنية والدولية'
          : 'Ligue Nationale de Football Amateur - Niveau 2 | Liens des instances officielles nationales et internationales'}
        icon={FaGlobe}
      />

      <main style={styles.main}>
        {SECTIONS.map((section) => (
          <section key={section.id} style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <section.icon style={styles.sectionIcon} />
              {isAr ? section.titleAr : section.titleFr}
            </h2>
            <div className="cards-grid-adaptive" style={styles.cardsGrid}>
              {section.links.map((link, idx) => (
                <LinkCard key={idx} link={link} section={section} isAr={isAr} />
              ))}
            </div>
          </section>
        ))}
      </main>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8f9fa',
    width: '100%',
    maxWidth: '100vw',
    overflowX: 'hidden'
  },
  main: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 16px 60px',
    boxSizing: 'border-box'
  },
  section: {
    marginBottom: '48px'
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: 'clamp(18px, 3vw, 22px)',
    fontWeight: 'bold',
    color: '#1a365d',
    marginBottom: '24px',
    paddingBottom: '12px',
    borderBottom: '3px solid #1a365d'
  },
  sectionIcon: {
    fontSize: '24px',
    color: '#1a365d'
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
    gap: '24px',
    width: '100%'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '1px solid #e8ecf0',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minHeight: '220px'
  },
  cardLogo: {
    width: '52px',
    height: '52px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #e8f0fe 0%, #d2e3fc 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px'
  },
  cardLogoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  cardLogoIcon: {
    fontSize: '24px',
    color: '#1a365d'
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1a1a1a',
    margin: '0 0 8px 0',
    lineHeight: 1.35
  },
  cardDesc: {
    fontSize: '14px',
    color: '#555',
    margin: '0 0 20px 0',
    lineHeight: 1.5,
    flex: 1
  },
  cardBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 18px',
    background: '#1a365d',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background 0.2s ease, transform 0.2s ease'
  },
  cardBtnIcon: {
    fontSize: '14px'
  }
};

if (typeof document !== 'undefined' && !document.getElementById('useful-links-page-styles')) {
  const sheet = document.createElement('style');
  sheet.id = 'useful-links-page-styles';
  sheet.textContent = `
    .useful-link-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    }
    .useful-link-btn:hover {
      background: #a01830;
      transform: scale(1.02);
    }
    @media (max-width: 768px) {
      .cards-grid-adaptive { grid-template-columns: 1fr !important; }
    }
    @media (min-width: 769px) and (max-width: 1023px) {
      .cards-grid-adaptive { grid-template-columns: repeat(2, 1fr) !important; }
    }
    @media (min-width: 1024px) {
      .cards-grid-adaptive { grid-template-columns: repeat(3, 1fr) !important; }
    }
    @media (min-width: 1280px) {
      .cards-grid-adaptive { grid-template-columns: repeat(4, 1fr) !important; }
    }
  `;
  document.head.appendChild(sheet);
}

export default UsefulLinksPage;
